import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
  Badge, Button
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  selectUploads,
  selectIsUploading,
  selectTotalProgress,
  selectShowNotification,
  selectBatchProgress,
  removeUpload,
  pauseUpload,
  resumeUpload,
  cancelUpload,
  setShowNotification,
  hideCompletedUploads
} from '../../store/slices/uploadSlice';

interface BackgroundUploadNotificationProps {
  onOpenUploadDialog?: () => void;
}

const BackgroundUploadNotification: React.FC<BackgroundUploadNotificationProps> = ({ 
  onOpenUploadDialog 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const uploads = useSelector(selectUploads);
  const isUploading = useSelector(selectIsUploading);
  const totalProgress = useSelector(selectTotalProgress);
  const showNotification = useSelector(selectShowNotification);
  const batchProgress = useSelector(selectBatchProgress);
  
  const [expanded, setExpanded] = useState(false);
  const [filePage, setFilePage] = useState(0);
  useEffect(() => setFilePage(page => Math.min(page, Math.max(0, Math.ceil(uploads.length / 50) - 1))), [uploads.length]);

  // Don't show if no uploads or notification is disabled
  if (!isUploading || !showNotification || uploads.length === 0) {
    return null;
  }

  const activeUploads = uploads.filter(u => u.status === 'uploading' || u.status === 'processing');
  const completedUploads = uploads.filter(u => u.status === 'completed');
  const failedUploads = uploads.filter(u => u.status === 'error');
  const pendingUploads = uploads.filter(u => u.status === 'pending');

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond === 0) return '0 B/s';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Calculate total bytes uploaded vs total bytes
  const calculateTotalBytes = () => {
    const included = uploads.filter(upload => !['cancelled', 'cancelling', 'error'].includes(upload.status));
    const totalBytes = included.reduce((sum, u) => sum + u.size, 0);
    const uploadedBytes = included.reduce((sum, u) => sum + (u.size * u.progress / 100), 0);
    return { totalBytes, uploadedBytes };
  };

  // Calculate overall upload speed (weighted average)
  const calculateOverallSpeed = () => {
    const activeFiles = uploads.filter(u => u.status === 'uploading' && u.speed);
    if (activeFiles.length === 0) return 0;
    const totalSpeed = activeFiles.reduce((sum, u) => sum + (u.speed || 0), 0);
    return totalSpeed;
  };

  const { totalBytes, uploadedBytes } = calculateTotalBytes();
  const overallSpeed = calculateOverallSpeed();
  const remainingSeconds = overallSpeed > 0 ? Math.ceil(Math.max(0, totalBytes - uploadedBytes) / overallSpeed) : null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'uploading':
        return <UploadIcon color="primary" />;
      case 'processing':
        return <UploadIcon color="secondary" />;
      case 'paused':
        return <PauseIcon color="warning" />;
      case 'cancelled':
        return <StopIcon color="disabled" />;
      default:
        return <UploadIcon color="disabled" />;
    }
  };

  const handlePauseResume = (uploadId: string, status: string) => {
    if (status === 'uploading') {
      dispatch(pauseUpload(uploadId));
    } else if (status === 'paused') {
      dispatch(resumeUpload(uploadId));
    }
  };

  const handleCancel = (uploadId: string, fileName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to cancel the upload of "${fileName}"?`
    );
    if (confirmed) {
      dispatch(cancelUpload(uploadId));
    }
  };

  const handleCancelAll = () => {
    const confirmed = window.confirm(
      `Are you sure you want to cancel all uploads?\n\nThis will cancel ${uploads.length} upload(s) in progress.`
    );
    if (confirmed) {
      uploads.forEach(upload => {
        if (upload.status === 'pending' || upload.status === 'uploading' || upload.status === 'processing') {
          dispatch(cancelUpload(upload.id));
        }
      });
    }
  };

  const handleClose = () => {
    dispatch(hideCompletedUploads());
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 1300,
        maxWidth: 400,
        minWidth: 300
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          cursor: onOpenUploadDialog ? 'pointer' : 'default',
          '&:hover': onOpenUploadDialog ? {
            boxShadow: 12,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out'
          } : {}
        }}
        onClick={onOpenUploadDialog}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Badge badgeContent={uploads.length} color="primary">
            <UploadIcon color="primary" />
          </Badge>
          <Typography variant="subtitle2" sx={{ ml: 1, flexGrow: 1 }}>
            Uploading Files
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
            {uploads.filter(u => u.status === 'pending' || u.status === 'uploading' || u.status === 'processing').length > 0 && (
              <Tooltip title="Cancel All">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelAll();
                  }}
                >
                  <StopIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={totalProgress}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {totalProgress}% complete
              {overallSpeed > 0 && <span> • {formatSpeed(overallSpeed)}</span>}
              {remainingSeconds !== null && remainingSeconds > 0 && <span> • about {Math.ceil(remainingSeconds / 60)} min remaining</span>}
              {batchProgress.totalBatches > 1 && (
                <span> • Batch {batchProgress.currentBatch}/{batchProgress.totalBatches}</span>
              )}
            </Typography>
            <Typography variant="caption" color="primary" fontWeight="medium">
              {formatFileSize(uploadedBytes)} / {formatFileSize(totalBytes)}
            </Typography>
          </Box>
        </Box>

        {/* Status Summary */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {batchProgress.totalBatches > 1 && (
            <Chip
              size="small"
              label={`Batch ${batchProgress.currentBatch}/${batchProgress.totalBatches}`}
              color="info"
              variant="outlined"
            />
          )}
          {activeUploads.length > 0 && (
            <Chip
              size="small"
              label={`${activeUploads.length} uploading`}
              color="primary"
              variant="outlined"
            />
          )}
          {pendingUploads.length > 0 && (
            <Chip
              size="small"
              label={`${pendingUploads.length} pending`}
              color="default"
              variant="outlined"
            />
          )}
          {completedUploads.length > 0 && (
            <Chip
              size="small"
              label={`${completedUploads.length} completed`}
              color="success"
              variant="outlined"
            />
          )}
          {failedUploads.length > 0 && (
            <Chip
              size="small"
              label={`${failedUploads.length} failed`}
              color="error"
              variant="outlined"
            />
          )}
        </Box>

        {/* Expanded Details */}
        <Collapse in={expanded}>
          {uploads.length > 50 && <Box sx={{display:'flex',gap:1}}>
            <Button disabled={filePage === 0} onClick={() => setFilePage(page => page - 1)}>Previous uploads</Button>
            <Typography>Page {filePage + 1} of {Math.ceil(uploads.length / 50)}</Typography>
            <Button disabled={(filePage + 1) * 50 >= uploads.length} onClick={() => setFilePage(page => page + 1)}>Next uploads</Button>
          </Box>}
          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
            {uploads.slice(filePage * 50, (filePage + 1) * 50).map((upload) => (
              <ListItem
                key={upload.id}
                sx={{
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {upload.type === 'folder' ? <FolderIcon /> : <FileIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="caption" noWrap>
                      {upload.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(upload.size)}
                        {upload.speed && ` • ${formatSpeed(upload.speed)}`}
                        {upload.status === 'processing' && ' • Processing on server...'}
                      </Typography>
                      {(upload.status === 'uploading' || upload.status === 'processing') && (
                        <LinearProgress
                          variant={upload.status === 'processing' ? 'indeterminate' : 'determinate'}
                          value={upload.progress}
                          sx={{ height: 2, mt: 0.5 }}
                        />
                      )}
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getStatusIcon(upload.status)}
                  {upload.status === 'uploading' && (
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={() => handleCancel(upload.id, upload.name)}
                      >
                        <StopIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {upload.status === 'paused' && (
                    <Tooltip title="Resume">
                      <IconButton
                        size="small"
                        onClick={() => handlePauseResume(upload.id, upload.status)}
                      >
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(upload.status === 'pending' || upload.status === 'paused') && (
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        onClick={() => handleCancel(upload.id, upload.name)}
                      >
                        <StopIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default BackgroundUploadNotification; 